import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Text,
  FormControl,
  FormLabel,
  Switch,
  Input,
  Select,
  VStack,
  useToast,
} from '@chakra-ui/react';

import {
  bulkDeleteSkuItemsApi,
  bulkDeleteListingsApi,
  downloadSkuDeleteReportCsvApi,
} from 'features/sku-management/sku-management.api';
import { parseSkuDeleteCsv, parseListingDeleteCsv } from '../utils/parseDeleteCsv.util';

const BulkDeleteModal = ({
  isOpen,
  onClose,
  mode,
  storeOptions = [],
  onCompleted,
}) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const [endMarketplace, setEndMarketplace] = useState(true);
  const [file, setFile] = useState(null);
  const [parsedCount, setParsedCount] = useState(null);
  const [storeId, setStoreId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isSkuMode = mode === 'sku-csv';
  const isListingMode = mode === 'listing-csv';

  const title = isSkuMode
    ? 'Bulk delete SKUs'
    : 'Bulk delete listings by eBay item ID';

  const handleFileChange = async (e) => {
    const nextFile = e.target.files?.[0];
    setFile(nextFile || null);
    setParsedCount(null);
    if (!nextFile) return;
    try {
      const ids = isSkuMode
        ? await parseSkuDeleteCsv(nextFile)
        : await parseListingDeleteCsv(nextFile);
      setParsedCount(ids.length);
    } catch {
      setParsedCount(0);
      toast({ title: 'Could not parse CSV', status: 'error', duration: 4000, isClosable: true });
    }
  };

  const handleSubmit = async () => {
    if (!file) {
      toast({ title: 'Select a CSV file', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    if (isListingMode && !storeId) {
      toast({ title: 'Select a store', status: 'warning', duration: 3000, isClosable: true });
      return;
    }

    setIsSubmitting(true);
    let result;

    if (isSkuMode) {
      result = await dispatch(
        bulkDeleteSkuItemsApi({ file, endMarketplaceListings: endMarketplace }),
      );
    } else {
      const ids = await parseListingDeleteCsv(file);
      result = await dispatch(
        bulkDeleteListingsApi({
          storeId,
          externalPlatformIds: ids,
          endOnMarketplace: endMarketplace,
        }),
      );
    }

    setIsSubmitting(false);

    if (
      (isSkuMode && bulkDeleteSkuItemsApi.fulfilled.match(result)) ||
      (isListingMode && bulkDeleteListingsApi.fulfilled.match(result))
    ) {
      toast({
        title: 'Bulk delete completed',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      onClose();
      onCompleted?.();
    } else {
      toast({
        title: 'Bulk delete failed',
        description: result.payload?.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDownloadReport = () => {
    dispatch(downloadSkuDeleteReportCsvApi());
  };

  const resetOnClose = () => {
    setFile(null);
    setParsedCount(null);
    setEndMarketplace(true);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={resetOnClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <Text fontSize="sm" color="gray.500">
              {isSkuMode
                ? 'Upload a CSV with a sku (or canonicalSku) column — one SKU per row.'
                : 'Upload a CSV with externalPlatformId or itemId column — eBay item IDs.'}
            </Text>
            {isListingMode && (
              <FormControl>
                <FormLabel>Store</FormLabel>
                <Select
                  placeholder="Select store"
                  value={storeId}
                  onChange={(e) => setStoreId(e.target.value)}
                >
                  {storeOptions.map((s) => (
                    <option key={s.id || s._id} value={s.id || s._id}>
                      {s.name || s.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl>
              <FormLabel>CSV file</FormLabel>
              <Input type="file" accept=".csv" onChange={handleFileChange} />
              {parsedCount != null && (
                <Text fontSize="sm" mt={1} color="gray.500">
                  {parsedCount.toLocaleString()} row(s) parsed
                </Text>
              )}
            </FormControl>
            <FormControl display="flex" alignItems="center">
              <FormLabel mb={0} flex={1}>
                {isSkuMode ? 'End listings on marketplace' : 'End listings on marketplace'}
              </FormLabel>
              <Switch
                isChecked={endMarketplace}
                onChange={(e) => setEndMarketplace(e.target.checked)}
                colorScheme="red"
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter gap={3} flexWrap="wrap">
          <Button size="sm" variant="outline" onClick={handleDownloadReport}>
            Download last report
          </Button>
          <Button variant="ghost" onClick={resetOnClose}>
            Cancel
          </Button>
          <Button
            colorScheme="red"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            isDisabled={!file || (isListingMode && !storeId)}
          >
            Run bulk delete
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default BulkDeleteModal;
